import type { HookAPI } from 'antd/es/modal/useModal'
import { useEffect, useRef } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { useAtomModal } from '@/components/Atoms/AtomModal/useAtomModal'
import AtomModalWithForm from '@/components/Atoms/AtomModalWithForm/AtomModalWithForm'
import AtomNotice from '@/components/Atoms/AtomNotice/AtomNotice'
import AtomSteps from '@/components/Atoms/AtomSteps/AtomSteps'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { BrandInterface } from '@/types'

import { getImportFileFormatByExtension } from '../lib/stockImportsFileFormats'
import { formatChecksResetAt } from '../lib/stockImportsFormat'
import {
  IMAP_PASSWORD_REENTER_MESSAGE,
  needsImapPasswordReentry,
} from '../lib/stockImportsValidation'
import type {
  ProductSupplierDto,
  StockImportConfigModel,
  StockImportConfigShortModel,
  StockImportRunMode,
  StoreModel,
} from '../types/stockImports.types'
import ConfigFooter from './ConfigFooter/ConfigFooter'
import cn from './ConfigModal.module.scss'
import ConfigReviewStep from './ConfigReviewStep/ConfigReviewStep'
import ConfigSourceStep from './ConfigSourceStep/ConfigSourceStep'
import { useConfigActions } from './hooks/useConfigActions'
import { useConfigFieldHandlers } from './hooks/useConfigFieldHandlers'
import { useConfigPreview } from './hooks/useConfigPreview'
import { useConfigPriceForm } from './hooks/useConfigPriceForm'
import { useConfigSourceValidation } from './hooks/useConfigSourceValidation'
import { useConfigSteps } from './hooks/useConfigSteps'
import {
  buildConfigFormDefaults,
  CONFIG_FORM_FIELDS,
  type ConfigFormInput,
  configFormResolver,
  type ConfigFormValues,
} from './lib/configFormModel'
import { getValidateSourceLabel } from './lib/configModalTexts'
import { buildConfigOptions } from './lib/configOptions'
import { buildSourceValidationKey } from './lib/configRequest'
import { buildConfigReviewSections } from './lib/configReviewSections'
import PricesStep from './PricesStep/PricesStep'
import SettingsStep from './SettingsStep/SettingsStep'

interface Props {
  open: boolean
  supplier: ProductSupplierDto | null
  suppliers: ProductSupplierDto[]
  supplierMode?: 'managed' | 'self'
  config: StockImportConfigModel | null
  stores: StoreModel[]
  configs: StockImportConfigShortModel[]
  storesLoading: boolean
  brands: BrandInterface[]
  brandsLoading: boolean
  canEditPrices?: boolean
  modal: HookAPI
  onClose: () => void
  onSaved: (config: StockImportConfigModel) => void
  onPersisted: (config: StockImportConfigShortModel) => void
  onRun: (
    config: StockImportConfigShortModel,
    mode: StockImportRunMode
  ) => Promise<boolean>
}

const SOURCE_KEY_FIELDS = [
  'sourceType',
  'url',
  'imapHost',
  'imapPort',
  'imapLogin',
  'imapPasswordEncrypted',
  'useSsl',
  'expectedSender',
  'subjectMask',
  'fileMask',
] as const

const isConfigFormField = (field: string): field is keyof ConfigFormInput =>
  CONFIG_FORM_FIELDS.some((name) => name === field)

function ConfigModal(props: Props) {
  const {
    open,
    config,
    supplierMode = 'managed',
    canEditPrices = false,
    modal,
    onClose,
  } = props
  const isSelfSupplierMode = supplierMode === 'self'
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const [atomModal, atomModalHolder] = useAtomModal()
  const formVersionRef = useRef(0)

  const form = useForm<ConfigFormInput, unknown, ConfigFormValues>({
    resolver: configFormResolver,
    defaultValues: buildConfigFormDefaults(
      config,
      props.supplier,
      isSelfSupplierMode
    ),
    mode: 'onTouched',
    shouldFocusError: false,
  })
  const { control, register, getValues, setError, trigger, watch } = form
  const { errors, isDirty } = form.formState
  const [sourceType] = useWatch({ control, name: [...SOURCE_KEY_FIELDS] })
  const [supplierId, storeId, url, fileFormat, cronExpression] = useWatch({
    control,
    name: ['supplierId', 'storeId', 'url', 'fileFormat', 'cronExpression'],
  })

  const rejectMaskedPassword = (values: ConfigFormInput) => {
    if (values.sourceType !== 'CUSTOM_IMAP') return false
    const storedMailbox = (actions.persistedConfig ?? config)?.sourceSettings
      ?.customImapSettings
    if (!needsImapPasswordReentry(values, storedMailbox)) return false
    setError('imapPasswordEncrypted', {
      message: IMAP_PASSWORD_REENTER_MESSAGE,
    })
    toast.error(IMAP_PASSWORD_REENTER_MESSAGE)
    return true
  }

  const prices = useConfigPriceForm({
    settings: config?.priceSettings,
    canEditPrices,
    isOrganisationSelected,
  })
  const preview = useConfigPreview(modal)
  const { invalidate: invalidatePreview } = preview
  const sourceValidation = useConfigSourceValidation({
    sourceType,
    currentKey: buildSourceValidationKey(getValues()),
    isMandatory: !config,
    configId: config?.id ?? null,
    modal,
    getValues,
    trigger,
    rejectMaskedPassword,
  })
  const actions = useConfigActions({
    props,
    form,
    atomModal,
    isSelfSupplierMode,
    isSourceValidated: sourceValidation.isValidated,
    formVersionRef,
    rejectMaskedPassword,
    prices,
  })
  const fieldHandlers = useConfigFieldHandlers({
    form,
    atomModal,
    isSelfSupplierMode,
  })
  const options = buildConfigOptions({
    ...props,
    savedConfigId: actions.savedConfig?.id,
    isSelfSupplierMode,
    supplierId,
    cronExpression,
  })
  const mismatchedStore = options.store.mismatchedStore
  const isStoreMismatched = !!storeId && storeId === mismatchedStore?.id
  const steps = useConfigSteps({
    sourceType,
    sourceStepFields: [
      ...(isSelfSupplierMode ? [] : ['supplierId' as const]),
      'storeId',
      'sourceType',
      'fileFormat',
      ...fieldHandlers.sourceStepFields(sourceType),
    ],
    isSourceValidated: sourceValidation.isValidated,
    isStoreMismatched,
    trigger,
    onStoreMismatch: () =>
      setError('storeId', {
        message: 'Склад передан другому поставщику — выберите другой',
      }),
    validateMainForm: actions.buildMainPayload,
    validateWholeForm: actions.buildPayload,
    onLeaveReview: invalidatePreview,
    hasPricesStep: prices.columns.visible,
    hasPriceSource: prices.hasPriceSource,
    hasColumnConflicts: () => prices.hasColumnConflicts(getValues()),
  })

  useEffect(() => {
    const subscription = watch(() => {
      formVersionRef.current += 1
      invalidatePreview()
      const fieldsWithErrors = Object.keys(form.formState.errors).filter(
        isConfigFormField
      )
      if (fieldsWithErrors.length > 0) void trigger(fieldsWithErrors)
    })
    return () => subscription.unsubscribe()
  }, [watch, invalidatePreview, form.formState, trigger])

  const validationResult = sourceValidation.result
  const detectedFileFormat =
    sourceType === 'URL' && validationResult?.success
      ? getImportFileFormatByExtension(validationResult.fileFormat)
      : null
  const context = { control, register, errors, isOrganisationSelected }

  return (
    <>
      {atomModalHolder}
      <AtomModalWithForm
        title={
          config
            ? 'Редактирование настройки загрузки'
            : 'Новая настройка загрузки'
        }
        open={open}
        width="min(1080px, calc(100vw - 32px))"
        submitting={actions.saving}
        maskClosable={false}
        onCancel={onClose}
        onSubmit={(event) => {
          event.preventDefault()
          if (steps.step === 'review') void actions.save()
          else void steps.goNext()
        }}
        beforeFields={
          <div className={cn.steps}>
            <AtomSteps
              options={steps.stepOptions}
              value={steps.step}
              onChange={steps.changeStep}
              ariaLabel="Шаг настройки"
              isOrganisationSelected={isOrganisationSelected}
            />
          </div>
        }
        footer={
          <ConfigFooter
            step={steps.step}
            config={config}
            saving={actions.saving}
            trialLoading={actions.trialLoading}
            runLoading={actions.runLoading}
            saveDisabled={!isDirty && !prices.isDirty}
            onCancel={onClose}
            onBack={steps.goBack}
            onTrialRun={() => void actions.saveAndRun('trial')}
            onLiveRun={actions.confirmLiveRun}
          />
        }
      >
        {config?.status === 'ACTIVE' && (
          <AtomNotice>
            После любого изменения настройка будет сохранена как черновик. Для
            повторной активации выполните загрузку с сохранением.
          </AtomNotice>
        )}
        {config?.checksResetAt && (
          <AtomNotice>{formatChecksResetAt(config.checksResetAt)}</AtomNotice>
        )}
        <ConfigSourceStep
          context={context}
          hidden={steps.step !== 'source'}
          isSelfSupplierMode={isSelfSupplierMode}
          supplierId={supplierId}
          sourceType={sourceType}
          fileFormat={fileFormat}
          supplierOptions={options.suppliers}
          storeOptions={options.store.options}
          sourceTypeOptions={options.sourceTypes}
          storesLoading={props.storesLoading}
          storesWithConfig={options.store.storesWithConfig}
          mismatchedStoreId={mismatchedStore?.id ?? null}
          mismatchedStoreName={
            isStoreMismatched ? (mismatchedStore?.description ?? null) : null
          }
          validation={
            sourceValidation.offersValidation
              ? {
                  label: getValidateSourceLabel(sourceType),
                  loading: sourceValidation.validating,
                  result: validationResult,
                  onValidate: () => void sourceValidation.validate(),
                }
              : null
          }
          detectedFileFormat={
            fileFormat && detectedFileFormat !== fileFormat
              ? detectedFileFormat
              : null
          }
          onSupplierChange={fieldHandlers.handleSupplierChange}
          onSourceTypeChange={(value) =>
            void fieldHandlers.handleSourceTypeChange(value)
          }
          onFileFormatChange={fieldHandlers.handleFileFormatChange}
          onFileMaskBlur={fieldHandlers.handleFileMaskBlur}
        />
        <SettingsStep
          context={context}
          hidden={steps.step !== 'settings'}
          sourceType={sourceType}
          fileFormat={fileFormat}
          url={url}
          cronOptions={options.cron}
          brandNameOptions={options.brandNames}
          brandOptions={options.brands}
          brandsLoading={props.brandsLoading}
          quantityHeaders={preview.preview?.quantityHeaders}
          priceColumns={prices.columns}
          onMappingChange={fieldHandlers.handleMappingChange}
          onPriceColumnChange={prices.setColumn}
          onUseQuantityHeader={fieldHandlers.handleUseQuantityHeader}
        />
        {canEditPrices && (
          <PricesStep
            hidden={steps.step !== 'prices'}
            prices={prices}
            isOrganisationSelected={isOrganisationSelected}
          />
        )}
        <ConfigReviewStep
          hidden={steps.step !== 'review'}
          sections={buildConfigReviewSections(getValues(), {
            isSelfSupplierMode,
            supplierOptions: options.suppliers,
            storeOptions: options.store.options,
            sourceTypeOptions: options.sourceTypes,
            cronOptions: options.cron,
            brandOptions: options.brands,
          })}
          preview={preview.preview}
          previewLoading={preview.loading}
          isOrganisationSelected={isOrganisationSelected}
          onPreview={() => void actions.runPreview(preview.loadPreview)}
        />
      </AtomModalWithForm>
    </>
  )
}

export default ConfigModal
